// --- MatchStateViewer.tsx ---
import { MatchState } from '@alienheadwars/football-card-manager-model';
// import necessary MUI components and icons
import ExitToAppIcon from '@mui/icons-material/ExitToApp'; // Example Icon
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material'; // Removed DialogActions
import { useState } from 'react';
// --- import ChoiceButtonsComponent and ButtonComponent ---
import { getCallableFunction } from '../../../../firebase/firebase'; // Adjust path relative to MatchStateViewer.ts as needed
import { ButtonComponent } from '../../../base/button/Button'; // Keep for Finish Game button
import { ChoiceButtonsComponent } from '../../../base/button/ChoiceButtons'; // import ChoiceButtonsComponent
import './Match.css';
// --- import localise ---
import { localise } from '../../../../locale/locale'; // Adjust path as needed
import { BoardComponent } from './boardComponents/board';
import { PlayerChoiceComponent } from './boardComponents/playerChoice';
import { CommentaryComponent } from './commentary/Commentary';
import {
  MatchViewContextComponent,
  MatchViewStatesIndex,
  useMatchViewState,
} from './matchViewContext/MatchViewContext';
import { ContextualScoreboard } from './scoreboard/Scoreboard';

export interface HasMatchState {
  matchState: MatchState;
}

export interface MatchStateProps extends HasMatchState {
  onComplete(): void;
}

export const MatchStateComponent = (props: MatchStateProps) => {
  const { matchState } = props;
  // --- State for confirmation dialog ---
  const [showConfirmQuit, setShowConfirmQuit] = useState(false);
  const [isQuitting, setIsQuitting] = useState(false); // To disable button during call

  // --- Handlers for quit confirmation ---
  const handleOpenConfirmQuit = () => {
    setShowConfirmQuit(true);
  };

  const handleCloseConfirmQuit = () => {
    if (isQuitting) return; // Prevent closing while action is in progress
    setShowConfirmQuit(false);
  };

  const handleConfirmQuit = async () => {
    if (isQuitting) return; // Prevent multiple calls
    setIsQuitting(true);
    try {
      const resignMatchFunction = getCallableFunction('resignMatch');
      await resignMatchFunction({ matchId: matchState.id });
      setShowConfirmQuit(false);
    } catch (error) {
      console.error('Error resigning match:', error);
      // Handle error display to user if needed
    } finally {
      setIsQuitting(false);
    }
  };

  return (
    <MatchViewContextComponent matchState={matchState}>
      <Stack
        direction="column"
        sx={{ width: 'min(50vh, 95vw)', height: '100%', position: 'relative' }}
        spacing={1}
        alignContent="center"
        // justifyContent='flex-start' alignItems='center'
      >
        {/* --- Quit Button --- */}
        {!matchState.matchFinished && (
          <IconButton
            aria-label="quit match"
            onClick={handleOpenConfirmQuit}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: 'white', // Adjust color as needed
              zIndex: 10,
            }}
            disabled={isQuitting}
          >
            <ExitToAppIcon />
          </IconButton>
        )}
        <ContextualScoreboard />
        <BoardComponent />
        <FinishMatchButton onComplete={props.onComplete} />
        <CommentaryComponent />
        <PlayerChoiceComponent />

        {/* --- UPDATED: Confirmation Dialog with ChoiceButtonsComponent --- */}
        <Dialog
          open={showConfirmQuit}
          onClose={handleCloseConfirmQuit} // Use the handler that checks isQuitting
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {localise('dialog.quitMatch.title')} {/* Example locale key */}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
              {' '}
              {/* Added margin bottom */}
              {localise('dialog.quitMatch.description')}{' '}
              {/* Example locale key */}
            </DialogContentText>
            {/* --- Use ChoiceButtonsComponent --- */}
            <ChoiceButtonsComponent
              primaryButtonText={localise('dialog.quitMatch.confirmButton')} // Example locale key
              primaryButtonAction={handleConfirmQuit}
              primaryButtonDisabled={isQuitting}
              secondaryButtonText={localise('dialog.quitMatch.cancelButton')} // Example locale key
              secondaryButtonAction={handleCloseConfirmQuit}
              secondaryButtonDisabled={isQuitting}
            />
          </DialogContent>
          {/* Removed <DialogActions> */}
        </Dialog>
      </Stack>
    </MatchViewContextComponent>
  );
};

const FinishMatchButton = (props: { onComplete(): void }) => {
  const matchViewState = useMatchViewState();
  if (
    !matchViewState.matchState.matchFinished || //always require match to be finished
    (matchViewState.currentRound.roundFinished &&
      matchViewState.matchViewStatesIndex < MatchViewStatesIndex.FINISHED) //If roundstate is finished but round is not finished, don't finish yet
  ) {
    return <></>;
  }
  return (
    <ButtonComponent onClick={props.onComplete}>Finish Game</ButtonComponent>
  );
};
